import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Aborts document-level listeners for desktop L1 mega menu behaviour
   * @type {AbortController | null}
   */
  #navMegaUiAbort = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Whether `wheel` can scroll `el` further in the direction of `deltaY`.
   * @param {Element} el
   * @param {number} deltaY
   * @returns {boolean}
   */
  #megaMenuWheelCanScrollElement(el, deltaY) {
    const style = getComputedStyle(el);
    const oy = style.overflowY;
    const overflowYScrollable =
      oy === "auto" || oy === "scroll" || oy === "overlay";

    if (!overflowYScrollable) return false;
    if (el.scrollHeight <= el.clientHeight + 1) return false;
    if (Math.abs(deltaY) < 0.001) return false;

    if (deltaY > 0) {
      return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    }

    return el.scrollTop > 0;
  }

  /**
   * Stops wheel events from scrolling the document when the pointer is over an open
   * mega menu but not over a scrollable region that can absorb the delta (tiered
   * sidebar, padding, etc.).
   * @param {WheelEvent} e
   */
  #handleMegaMenuWheelDocumentCapture = (e) => {
    const raw = e.target;
    const start =
      raw instanceof Element
        ? raw
        : raw instanceof Node
          ? raw.parentElement
          : null;

    if (!start) return;

    const menu = start.closest(".mega-menu");
    if (!menu) return;

    if (!menu.closest(".nav-desktop__item.has-mega-menu")) return;

    const nav = this.querySelector(".nav-desktop");
    if (!nav?.contains(menu)) return;

    const menuStyle = getComputedStyle(menu);
    if (menuStyle.visibility === "hidden" || menuStyle.pointerEvents === "none") {
      return;
    }

    for (let el = start; el; el = el.parentElement) {
      if (this.#megaMenuWheelCanScrollElement(el, e.deltaY)) {
        return;
      }

      if (el === menu) {
        break;
      }
    }

    e.preventDefault();
  };

  /**
   * Total document scroll (cumulative, up or down) required before toggling sticky
   * reveal/hide. Same threshold both directions so trackpad “quick” scroll feels even.
   * @constant {number}
   */
  #scrollRevealTotalPx = 40;

  /**
   * Max pixels counted toward {@link #scrollRevealTotalPx} in one coalesced tick (Safari
   * rubber-band can report a huge delta in one frame).
   * @constant {number}
   */
  #scrollRevealPerTickCapPx = 100;

  /**
   * Ignore tiny |delta| for accumulation so sub-pixel jitter does not move totals.
   * @constant {number}
   */
  #scrollMicroDeltaPx = 2;

  /**
   * Running sum of upward scroll toward {@link #scrollRevealTotalPx} (scroll-up mode + always).
   * @type {number}
   */
  #scrollUpAccumulatedPx = 0;

  /**
   * Running sum of downward scroll toward {@link #scrollRevealTotalPx} (symmetric hide).
   * @type {number}
   */
  #scrollDownAccumulatedPx = 0;

  /**
   * Track natural header position to fix positioning issues
   * @type {number}
   */
  #headerOffsetTop = 0;

  /**
   * Refcount: while > 0, skip sticky scroll-up / always scroll logic (modals, account sheet, body scroll lock).
   * @type {number}
   */
  #stickyScrollSuspendCount = 0;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty(
      "--header-height",
      `${roundedHeaderHeight}px`
    );

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Fixes mega menu positioning when page is partially scrolled.
   */
  #initMegaMenuPositioning = () => {
    const navItems = this.querySelectorAll(".nav-desktop__item.has-mega-menu");

    navItems.forEach((item) => {
      const megaMenu = item.querySelector(".mega-menu");
      if (!megaMenu) return;

      item.addEventListener("mouseenter", () => {
        const headerRect = this.getBoundingClientRect();
        const offset = 15; // Tweak this to match header padding
        megaMenu.style.top = `${headerRect.bottom - offset}px`;
      });
    });
  };

  /**
   * Closes desktop mega menus opened via click/touch helpers.
   */
  #closeDesktopMegaMenus() {
    const nav = this.querySelector(".nav-desktop");
    if (!nav) return;

    for (const item of nav.querySelectorAll(".nav-desktop__item.has-mega-menu")) {
      item.classList.remove("mega-menu-open", "touch-menu-open");
      const link = item.querySelector(".nav-desktop__link");
      if (link?.hasAttribute("aria-expanded")) {
        link.setAttribute("aria-expanded", "false");
      }
    }

    document.body.classList.remove("mega-tiered-escape");
  }

  /**
   * Desktop L1: hover-only, hover + navigate on click, or click-to-toggle (section `l1_menu_behaviour`).
   */
  #initDesktopNavMegaMenu() {
    this.#navMegaUiAbort?.abort();
    this.#navMegaUiAbort = new AbortController();
    const signal = this.#navMegaUiAbort.signal;

    const nav = this.querySelector(".nav-desktop");
    if (!nav) return;

    document.addEventListener(
      "wheel",
      this.#handleMegaMenuWheelDocumentCapture,
      { capture: true, passive: false, signal },
    );

    const behaviour = nav.dataset.l1MenuBehaviour || "hover";
    const items = nav.querySelectorAll(".nav-desktop__item.has-mega-menu");
    const isTouch =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    const closeAllClickOpen = () => {
      this.#closeDesktopMegaMenus();
    };

    if (behaviour === "click_open") {
      for (const item of items) {
        const link = item.querySelector(".nav-desktop__link");
        if (!link) continue;

        link.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            const open = item.classList.contains("mega-menu-open");
            if (open) {
              item.classList.remove("mega-menu-open");
              link.setAttribute("aria-expanded", "false");
            } else {
              for (const other of items) {
                if (other === item) continue;
                other.classList.remove("mega-menu-open");
                const otherLink = other.querySelector(".nav-desktop__link");
                otherLink?.setAttribute("aria-expanded", "false");
              }
              item.classList.add("mega-menu-open");
              link.setAttribute("aria-expanded", "true");
            }
          },
          { signal },
        );
      }

      document.addEventListener(
        "mousedown",
        (e) => {
          const t = e.target;
          if (!(t instanceof Node)) return;
          if (t.closest?.(".nav-desktop__item.has-mega-menu")) return;
          if (t.closest?.(".mega-menu")) return;
          closeAllClickOpen();
        },
        { capture: true, signal },
      );

      const overlay = document.querySelector(".mega-menu-overlay");
      overlay?.addEventListener(
        "click",
        () => {
          closeAllClickOpen();
        },
        { signal },
      );

      document.addEventListener(
        "keydown",
        (e) => {
          if (e.key !== "Escape") return;
          closeAllClickOpen();
        },
        { capture: true, signal },
      );
    } else if (behaviour === "hover_click") {
      if (!isTouch) return;

      for (const item of items) {
        const link = item.querySelector(".nav-desktop__link");
        if (!link?.hasAttribute("data-clickable-menu")) continue;

        link.addEventListener(
          "click",
          (e) => {
            if (item.classList.contains("touch-menu-open")) return;
            e.preventDefault();
            for (const other of items) {
              other.classList.remove("touch-menu-open");
            }
            item.classList.add("touch-menu-open");
          },
          { signal },
        );
      }

      document.addEventListener(
        "click",
        (e) => {
          const t = e.target;
          if (!(t instanceof Node)) return;
          for (const openItem of items) {
            if (!openItem.classList.contains("touch-menu-open")) continue;
            if (openItem.contains(t)) continue;
            openItem.classList.remove("touch-menu-open");
          }
        },
        { signal },
      );
    } else {
      // behaviour === "hover": fine pointers use CSS :hover; touch / coarse pointers cannot rely on
      // @media (hover: hover) + :hover — use .touch-menu-open like hover_click (see header.css overlay rules).
      for (const item of items) {
        const link = item.querySelector(".nav-desktop__link");
        if (!link) continue;

        link.addEventListener(
          "click",
          (e) => {
            if (!isTouch) {
              e.preventDefault();
              return;
            }

            const touchOpen = item.classList.contains("touch-menu-open");
            if (touchOpen) {
              e.preventDefault();
              item.classList.remove("touch-menu-open");
              return;
            }

            e.preventDefault();
            for (const other of items) {
              other.classList.remove("touch-menu-open");
            }
            item.classList.add("touch-menu-open");
          },
          { signal },
        );
      }

      if (isTouch) {
        document.addEventListener(
          "click",
          (e) => {
            const t = e.target;
            if (!(t instanceof Node)) return;
            for (const openItem of items) {
              if (!openItem.classList.contains("touch-menu-open")) continue;
              if (openItem.contains(t)) continue;
              openItem.classList.remove("touch-menu-open");
            }
          },
          { signal },
        );

        document.addEventListener(
          "keydown",
          (e) => {
            if (e.key !== "Escape") return;
            let hadTouchOpen = false;
            for (const item of items) {
              if (item.classList.contains("touch-menu-open")) {
                hadTouchOpen = true;
                item.classList.remove("touch-menu-open");
              }
            }
            if (!hadTouchOpen) return;
            document.body.classList.remove("mega-tiered-escape");
            e.stopImmediatePropagation();
          },
          { capture: true, signal },
        );
      }
    }
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        if (this.dataset.themeColor)
          changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
        // Update natural position whenever we're NOT fixed
        if (isIntersecting && this.dataset.stickyState !== "active") {
          this.#headerOffsetTop = this.offsetTop;
        }
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove("desktop:hidden");
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add("hidden");
    } else {
      this.refs.headerDrawerContainer.classList.add("desktop:hidden");
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove("hidden");
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * @param {Event} event
   */
  #handleHeaderStickyScrollSuspend = (event) => {
    const detail = /** @type {CustomEvent<{ delta?: number; syncDocumentScrollTop?: number }>} */ (
      event
    ).detail;
    if (!detail || typeof detail.delta !== "number") return;

    const wasSuspended = this.#stickyScrollSuspendCount > 0;
    this.#stickyScrollSuspendCount = Math.max(
      0,
      this.#stickyScrollSuspendCount + detail.delta,
    );
    const nowSuspended = this.#stickyScrollSuspendCount > 0;

    if (typeof detail.syncDocumentScrollTop === "number") {
      this.#lastScrollTop = detail.syncDocumentScrollTop;
      this.#scrollUpAccumulatedPx = 0;
      this.#scrollDownAccumulatedPx = 0;
    }

    if (!wasSuspended && nowSuspended) {
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }
      this.removeAttribute("data-animating");
      const stickyMode = this.getAttribute("sticky");
      if (
        stickyMode === "scroll-up" &&
        this.#offscreen &&
        this.dataset.stickyState === "idle"
      ) {
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      }
    }

    if (wasSuspended && !nowSuspended) {
      requestAnimationFrame(() => this.#updateScrollState());
    }
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute("sticky");
    if (this.#stickyScrollSuspendCount > 0) return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;

    if (stickyMode === "scroll-up" && scrollTop <= this.#headerOffsetTop) {
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }
      this.removeAttribute("data-animating");
      this.#offscreen = false;
      this.#scrollUpAccumulatedPx = 0;
      this.#scrollDownAccumulatedPx = 0;
      this.dataset.stickyState = "inactive";
      this.dataset.scrollDirection = "none";
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!this.#offscreen && stickyMode !== "always") {
      this.#lastScrollTop = scrollTop;
      this.#scrollUpAccumulatedPx = 0;
      this.#scrollDownAccumulatedPx = 0;
      return;
    }

    const delta = scrollTop - this.#lastScrollTop;
    const micro = this.#scrollMicroDeltaPx;
    const cap = this.#scrollRevealPerTickCapPx;
    const total = this.#scrollRevealTotalPx;

    if (delta < -micro) {
      this.#scrollUpAccumulatedPx += Math.min(-delta, cap);
      this.#scrollDownAccumulatedPx = 0;
    } else if (delta > micro) {
      this.#scrollDownAccumulatedPx += Math.min(delta, cap);
      this.#scrollUpAccumulatedPx = 0;
    }

    const isScrollingUpIntent = this.#scrollUpAccumulatedPx >= total;
    const isScrollingDownIntent = this.#scrollDownAccumulatedPx >= total;
    if (isScrollingUpIntent) {
      this.#scrollUpAccumulatedPx = 0;
    }
    if (isScrollingDownIntent) {
      this.#scrollDownAccumulatedPx = 0;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === "always") {
      const isAtTop = scrollTop <= this.#headerOffsetTop;

      if (isAtTop) {
        this.dataset.scrollDirection = "none";
        this.#scrollUpAccumulatedPx = 0;
        this.#scrollDownAccumulatedPx = 0;
      } else if (isScrollingUpIntent) {
        this.dataset.scrollDirection = "up";
      } else if (isScrollingDownIntent) {
        this.dataset.scrollDirection = "down";
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUpIntent) {
      this.removeAttribute("data-animating");
      this.dataset.stickyState = "active";
      this.dataset.scrollDirection = "up";
    } else if (isScrollingDownIntent) {
      if (this.dataset.stickyState === "active") {
        this.dataset.scrollDirection = "none";
        this.setAttribute("data-animating", "");

        this.#timeout = setTimeout(() => {
          this.dataset.stickyState = "idle";
          this.removeAttribute("data-animating");
        }, this.#animationDelay);
      } else {
        this.dataset.scrollDirection = "none";
        this.dataset.stickyState = "idle";
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);

    // Capture initial natural position
    this.#headerOffsetTop = this.offsetTop;

    this.#initMegaMenuPositioning();
    this.#initDesktopNavMegaMenu();

    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
        document.addEventListener(
          ThemeEvents.headerStickyScrollSuspend,
          this.#handleHeaderStickyScrollSuspend,
        );
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#navMegaUiAbort?.abort();
    this.#navMegaUiAbort = null;
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
    document.removeEventListener("scroll", this.#handleWindowScroll);
    document.removeEventListener(
      ThemeEvents.headerStickyScrollSuspend,
      this.#handleHeaderStickyScrollSuspend,
    );
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = document.querySelector("#header-group");

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute("transparent") &&
            header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty(
        "--header-group-height",
        `${roundedHeaderGroupHeight}px`
      );
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
